import { LazyPageLoading } from 'components/Common/LazyPageLoading'
import { lazy } from 'react'
import { Route, Routes } from 'react-router-dom'
import { ProjectDeliveryEstimationSettings } from './VirtualFactory/ProjectDeliveryEstimationSettings/ProjectDeliveryEstimationSettings'

const AddressBookImportResult = lazy(
  () => import('features/AddressBook/AddressBookImportResult')
)
const CustomerAddressBook = lazy(
  () => import('features/AddressBook/CustomerAddressBook')
)
const AutoAddWorkingStepsAtProjectLevelRulesPage = lazy(
  () =>
    import(
      'features/BusinessRules/AddWorkingStepsAtProjectLevelRules/AutoAddWorkingStepsAtProjectLevelRulesPage'
    )
)
const AutoAddWorkingStepsRulesPage = lazy(
  () =>
    import(
      'features/BusinessRules/AddWorkingStepsRules/AutoAddWorkingStepsRulesPage'
    )
)
const AutoAssignMaterialsRulesPage = lazy(
  () =>
    import(
      'features/BusinessRules/AutoAssignMaterialsRules/AutoAssignMaterialsRulesPage'
    )
)
const AutoQuoteRulesPage = lazy(
  () => import('features/BusinessRules/AutoQuoteRules/AutoQuoteRulesPage')
)
const AutoStartManufacturabilityCheckRulesPage = lazy(
  () =>
    import(
      'features/BusinessRules/AutoStartManufacturabilityCheckRules/AutoStartManufacturabilityCheckRules'
    )
)
const RemnantRulesPage = lazy(
  () => import('features/BusinessRules/RemnantRules/RemnantRulesPage')
)

const NestRulesPage = lazy(
  () => import('features/BusinessRules/NestRules/NestRules')
)

const DxfDrawingsSettings = lazy(
  () => import('./VirtualFactory/Drawings/DxfDrawingsSettings')
)

const IntegrationStore = lazy(
  () => import('features/Integrations/Store/IntegrationStore')
)
const QuotationTemplatesPage = lazy(
  () =>
    import(
      './VirtualFactory/Documents/QuotationTemplates/QuotationTemplatesPage'
    )
)
const AgentSettings = lazy(
  () => import('./VirtualFactory/Integrations/Agent/AgentSettings')
)
const CatalogIntegrationSettings = lazy(
  () =>
    import(
      './VirtualFactory/Integrations/CatalogIntegration/CatalogIntegration'
    )
)
const WebHooksConfiguration = lazy(
  () => import('./VirtualFactory/Integrations/WebHooks/WebHooksConfiguration')
)

const ImnooIntegrationPage = lazy(
  () => import('features/Integrations/ImnooSettings/ImnooSettings.page')
)

const Werk24Page = lazy(
  () => import('features/Integrations/Werk24Settings/Werk24.page')
)

const NestingSettings = lazy(
  () =>
    import('./VirtualFactory/Materials/CommercialNesting/NewNestingSettings')
)

const MachineLearningQueryDataPage = lazy(
  () =>
    import(
      'features/MachineLearning/MachineLearningTrainingData/MachineLearningQueryDataPage'
    )
)
const MachineLearningTrainingData = lazy(
  () =>
    import(
      'features/MachineLearning/MachineLearningTrainingData/MachineLearningTrainingData'
    )
)
const ResourcesAndActivities = lazy(
  () =>
    import(
      './VirtualFactory/Capabilities/ResourcesAndActivities/ResourcesAndActivities'
    )
)
const WorkingStepsSurchargesAndDiscountsPage = lazy(
  () =>
    import(
      './VirtualFactory/Capabilities/WorkingStepsSurchargesAndDiscounts/WorkingStepsSurchargesAndDiscountsPage'
    )
)

const ArticleKeywordsPage = lazy(
  () =>
    import('./VirtualFactory/Materials/ArticleManagement/ArticleKeywordsPage')
)

const ArticleManagement = lazy(
  () => import('./VirtualFactory/Materials/ArticleManagement/ArticleManagement')
)

const ArticleManagementImportResult = lazy(
  () =>
    import(
      './VirtualFactory/Materials/ArticleManagement/ArticleManagementImportResult'
    )
)

const MachiningSettingsPage = lazy(
  () => import('./VirtualFactory/MachiningSettings/MachiningSettingsPage')
)

const MaterialsAndStandardsSettings = lazy(
  () =>
    import(
      './VirtualFactory/Materials/MaterialsAndStandards/MaterialsAndStandards'
    )
)
const MaterialSurchargesAndDiscountsPage = lazy(
  () =>
    import(
      './VirtualFactory/Materials/MaterialSurchargesAndDiscounts/MaterialSurchargesAndDiscountsPage'
    )
)
const PurchasingPriceSettings = lazy(
  () =>
    import('./VirtualFactory/Materials/PurchasingPrice/PurchasingPriceSettings')
)

const AvailableMaterials = lazy(
  () =>
    import(
      './CustomerPortal/ResourcesAndActivities/Materials/AvailableMaterials'
    )
)
const AvailableWorkingSteps = lazy(
  () =>
    import(
      './CustomerPortal/ResourcesAndActivities/WorkingSteps/AvailableWorkingSteps'
    )
)

const PortalAvailableArticles = lazy(
  () => import('./CustomerPortal/Articles/PortalAvailableArticles')
)

const EmailSettingsPage = lazy(
  () => import('features/EmailSettings/EmailSettings.page')
)
const ConnectedParties = lazy(
  () => import('features/CustomerPortal/ConnectedParties/ConnectedParties')
)
const CustomerPortalSettings = lazy(
  () => import('features/CustomerPortal/PortalSettings/CustomerPortalSettings')
)

const UserProfilePage = lazy(() => import('./General/UserProfilePage'))
const OrganizationProfile = lazy(
  () => import('./General/OrganizationProfile/PartyProfile')
)
const SystemUserManagement = lazy(
  () =>
    import('features/UserManagement/SystemUserManagement/SystemUserManagement')
)

const ExportTemplates = lazy(
  () =>
    import(
      'features/ProjectExportTemplates/Settings/ExportTemplatesSettingsPage'
    )
)

const EmailTemplatesPage = lazy(
  () => import('./CustomerPortal/EmailTemplates/EmailTemplatesPage')
)

const CadFilePatternsPage = lazy(
  () => import('./General/Regex/CadFilePatterns.page')
)

export const SettingsRoutes = () => {
  return (
    <Routes>
      <Route
        path="general/user"
        element={
          <LazyPageLoading>
            <UserProfilePage />
          </LazyPageLoading>
        }
      />

      <Route
        path="general/organization"
        element={
          <LazyPageLoading>
            <OrganizationProfile />
          </LazyPageLoading>
        }
      />

      <Route
        path="general/user-management"
        element={
          <LazyPageLoading>
            <SystemUserManagement />
          </LazyPageLoading>
        }
      />

      <Route
        path="portal/materials"
        element={
          <LazyPageLoading>
            <AvailableMaterials />
          </LazyPageLoading>
        }
      />

      <Route
        path="portal/working-steps"
        element={
          <LazyPageLoading>
            <AvailableWorkingSteps />
          </LazyPageLoading>
        }
      />

      <Route
        path="portal/articles/available-articles"
        element={
          <LazyPageLoading>
            <PortalAvailableArticles />
          </LazyPageLoading>
        }
      />

      <Route
        path="portal/connected-organizations"
        element={
          <LazyPageLoading>
            <ConnectedParties />
          </LazyPageLoading>
        }
      />

      <Route
        path="portal/sdk-settings"
        element={
          <LazyPageLoading>
            <CustomerPortalSettings />
          </LazyPageLoading>
        }
      />

      <Route
        path="portal/email-settings"
        element={
          <LazyPageLoading>
            <EmailSettingsPage />
          </LazyPageLoading>
        }
      />

      <Route path="portal/email-templates/*" element={<EmailTemplatesPage />} />

      <Route
        path="virtual-factory/articles/management"
        element={
          <LazyPageLoading>
            <ArticleManagement />
          </LazyPageLoading>
        }
      />

      <Route
        path="virtual-factory/articles/pricing"
        element={
          <LazyPageLoading>
            <PurchasingPriceSettings />
          </LazyPageLoading>
        }
      />

      <Route
        path="virtual-factory/articles/discount-surcharge"
        element={
          <LazyPageLoading>
            <MaterialSurchargesAndDiscountsPage />
          </LazyPageLoading>
        }
      />

      <Route
        path="general/machining-settings"
        element={
          <LazyPageLoading>
            <MachiningSettingsPage />
          </LazyPageLoading>
        }
      />

      <Route
        path="virtual-factory/articles/article-import"
        element={
          <LazyPageLoading>
            <ArticleManagementImportResult />
          </LazyPageLoading>
        }
      />

      <Route
        path="virtual-factory/articles/keywords"
        element={
          <LazyPageLoading>
            <ArticleKeywordsPage />
          </LazyPageLoading>
        }
      />

      <Route
        path="virtual-factory/materials/standards"
        element={
          <LazyPageLoading>
            <MaterialsAndStandardsSettings />
          </LazyPageLoading>
        }
      />

      <Route
        path="virtual-factory/capabilities/resources"
        element={
          <LazyPageLoading>
            <ResourcesAndActivities />
          </LazyPageLoading>
        }
      />

      <Route
        path="virtual-factory/capabilities/discount-surcharge"
        element={
          <LazyPageLoading>
            <WorkingStepsSurchargesAndDiscountsPage />
          </LazyPageLoading>
        }
      />

      <Route
        path="virtual-factory/project-delivery-estimation"
        element={
          <LazyPageLoading>
            <ProjectDeliveryEstimationSettings />
          </LazyPageLoading>
        }
      />

      <Route
        path="capabilities/training-data"
        element={
          <LazyPageLoading>
            <MachineLearningTrainingData />
          </LazyPageLoading>
        }
      />

      <Route
        path="capabilities/query-training-data"
        element={
          <LazyPageLoading>
            <MachineLearningQueryDataPage />
          </LazyPageLoading>
        }
      />

      <Route
        path="general/export/templates"
        element={
          <LazyPageLoading>
            <ExportTemplates />
          </LazyPageLoading>
        }
      />

      <Route
        path="general/nesting/default"
        element={
          <LazyPageLoading>
            <NestingSettings />
          </LazyPageLoading>
        }
      />

      <Route
        path="integrations/material-catalog"
        element={
          <LazyPageLoading>
            <CatalogIntegrationSettings />
          </LazyPageLoading>
        }
      />

      <Route
        path="integrations/webhooks"
        element={
          <LazyPageLoading>
            <WebHooksConfiguration />
          </LazyPageLoading>
        }
      />

      <Route
        path="integrations/agents"
        element={
          <LazyPageLoading>
            <AgentSettings />
          </LazyPageLoading>
        }
      />

      <Route
        path="integrations/store"
        element={
          <LazyPageLoading>
            <IntegrationStore />
          </LazyPageLoading>
        }
      />

      <Route
        path="integrations/imnoo"
        element={
          <LazyPageLoading>
            <ImnooIntegrationPage />
          </LazyPageLoading>
        }
      />

      <Route
        path="integrations/werk24"
        element={
          <LazyPageLoading>
            <Werk24Page />
          </LazyPageLoading>
        }
      />

      {/* TODO: Verify if this page already exists */}
      {/* <Route
        path="integrations/webapp"
        element={
          <LazyPageLoading>
            <WebApp />
          </LazyPageLoading>
        }
      /> */}

      <Route
        path="documents/templates"
        element={
          <LazyPageLoading>
            <QuotationTemplatesPage />
          </LazyPageLoading>
        }
      />

      <Route
        path="general/drawings/dxf"
        element={
          <LazyPageLoading>
            <DxfDrawingsSettings />
          </LazyPageLoading>
        }
      />

      <Route
        path="general/drawings/cad-file-patterns"
        element={
          <LazyPageLoading>
            <CadFilePatternsPage />
          </LazyPageLoading>
        }
      />

      <Route
        path="rules/auto-quote"
        element={
          <LazyPageLoading>
            <AutoQuoteRulesPage />
          </LazyPageLoading>
        }
      />

      <Route
        path="rules/auto-add-working-steps"
        element={
          <LazyPageLoading>
            <AutoAddWorkingStepsRulesPage />
          </LazyPageLoading>
        }
      />

      <Route
        path="rules/auto-add-working-steps-at-project-level"
        element={
          <LazyPageLoading>
            <AutoAddWorkingStepsAtProjectLevelRulesPage />
          </LazyPageLoading>
        }
      />

      <Route
        path="rules/auto-assign-materials"
        element={
          <LazyPageLoading>
            <AutoAssignMaterialsRulesPage />
          </LazyPageLoading>
        }
      />

      <Route
        path="rules/auto-start-manufacturability-check"
        element={
          <LazyPageLoading>
            <AutoStartManufacturabilityCheckRulesPage />
          </LazyPageLoading>
        }
      />

      <Route
        path="rules/remnants"
        element={
          <LazyPageLoading>
            <RemnantRulesPage />
          </LazyPageLoading>
        }
      />

      <Route
        path="rules/nest"
        element={
          <LazyPageLoading>
            <NestRulesPage />
          </LazyPageLoading>
        }
      />

      <Route
        path="customers/address-book"
        element={
          <LazyPageLoading>
            <CustomerAddressBook />
          </LazyPageLoading>
        }
      />

      <Route
        path="customers/address-book-import"
        element={
          <LazyPageLoading>
            <AddressBookImportResult />
          </LazyPageLoading>
        }
      />

      <Route
        index
        element={
          <div className="no-active-configuration">
            Select a configuration...
          </div>
        }
      />
    </Routes>
  )
}
